import { SiteHeader } from "@/components/site-header"
import { Link } from "react-router-dom"
import { ArrowLeftIcon } from "lucide-react"

export default function WarningsCalculationPage() {
  return (
    <>
      <SiteHeader title="Cálculo de Alertas en CRTs" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="w-full space-y-8">

          {/* ─── Intro ─── */}
          <div>
            <Link
              to="/docs"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeftIcon className="size-3" /> Documentación
            </Link>
            <h2 className="text-base font-semibold mb-1">Cálculo de Alertas en CRTs SmartGT</h2>
            <p className="text-sm text-muted-foreground">
              Define cómo se calculan las alertas (warnings) en los CRTs, qué campos se validan,
              y cuál es el tratamiento especial para CRTs fleteros CDC.
            </p>
          </div>

          {/* ─── Visión General ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Visión General</h3>
            <div className="text-sm space-y-3 text-muted-foreground">
              <p>
                Cada CRT (Comprobante de Recepción de Transporte) tiene un campo <code className="text-xs bg-muted px-1 py-0.5 rounded">warning_count</code> que
                acumula el número de problemas encontrados durante la validación.
              </p>
              <p>
                El cálculo se ejecuta automáticamente después de importar datos y puede recalcularse manualmente con
                <code className="text-xs bg-muted px-1 py-0.5 rounded">POST /smart-gt/recalculate-warnings</code>.
              </p>
            </div>
          </section>

          {/* ─── Campos Validados ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Campos Validados (Nivel CRT)</h3>
            <div className="space-y-3">
              <div className="border rounded-lg p-3">
                <div className="text-xs font-mono bg-muted px-2 py-1 rounded mb-2">product</div>
                <p className="text-sm text-muted-foreground">
                  Si <code className="text-xs">product</code> no es NULL, debe existir un producto registrado
                  en la tabla <code className="text-xs">products</code> cuyo <code className="text-xs">sgt_codes</code> contenga
                  el valor (sin importar mayúsculas/minúsculas).
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si no existe → <strong>+1 warning</strong>
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs font-mono bg-muted px-2 py-1 rounded mb-2">client</div>
                <p className="text-sm text-muted-foreground">
                  Si <code className="text-xs">client</code> no es NULL, debe existir un cliente registrado
                  en la tabla <code className="text-xs">clients</code> cuyo <code className="text-xs">sgt_codes</code> contenga
                  el valor.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si no existe → <strong>+1 warning</strong>
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs font-mono bg-muted px-2 py-1 rounded mb-2">shipper_city / loading_city</div>
                <p className="text-sm text-muted-foreground">
                  Si cualquiera de estos campos no es NULL (preferencia: <code className="text-xs">shipper_city</code> primero),
                  debe existir una ciudad registrada en <code className="text-xs">cities</code>.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si no existe → <strong>+1 warning</strong>
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs font-mono bg-muted px-2 py-1 rounded mb-2">recipient_city / unloading_city</div>
                <p className="text-sm text-muted-foreground">
                  Si cualquiera de estos campos no es NULL (preferencia: <code className="text-xs">recipient_city</code> primero),
                  debe existir una ciudad registrada en <code className="text-xs">cities</code>.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si no existe → <strong>+1 warning</strong>
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs font-mono bg-muted px-2 py-1 rounded mb-2">route (origen → destino)</div>
                <p className="text-sm text-muted-foreground">
                  Si ambas ciudades (origen y destino) existen en el sistema, debe haber una ruta registrada
                  que conecte esas dos ciudades y no esté eliminada (<code className="text-xs">deleted_at IS NULL</code>).
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si no existe la ruta → <strong>+1 warning</strong>
                </p>
              </div>
            </div>
          </section>

          {/* ─── Campos de Detalle ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Campos Validados (Detalles del CRT)</h3>
            <p className="text-sm text-muted-foreground">
              Los detalles de un CRT se validan y sus warnings se agregan al total del CRT.
            </p>

            <div className="space-y-3">
              <div className="border rounded-lg p-3">
                <div className="text-xs font-mono bg-muted px-2 py-1 rounded mb-2">driver_name (chofer)</div>
                <p className="text-sm text-muted-foreground">
                  Si <code className="text-xs">driver_name</code> no es NULL, debe existir un chofer registrado
                  en <code className="text-xs">drivers</code> cuyo <code className="text-xs">sgt_codes</code> contenga el valor.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si no existe → <strong>+1 warning por detalle</strong>
                </p>
                <p className="text-xs text-amber-600 font-medium mt-2">
                  ⚠️ EXCEPCIÓN: En CRTs con prefijo CDC (<code className="text-xs">AR180</code>, <code className="text-xs">BR180</code>, etc.),
                  este check se omite porque los choferes fleteros inexistentes se reemplazan automáticamente con
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">"FLETEROS VARIOS"</code> en los exports.
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs font-mono bg-muted px-2 py-1 rounded mb-2">truck_plate (placa camión)</div>
                <p className="text-sm text-muted-foreground">
                  Si <code className="text-xs">truck_plate</code> no es NULL, debe existir una unidad registrada
                  en <code className="text-xs">units</code> cuyo <code className="text-xs">code</code> coincida
                  (comparación case-insensitive).
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si no existe → <strong>+1 warning por detalle</strong>
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="text-xs font-mono bg-muted px-2 py-1 rounded mb-2">trailer_plate (placa remolque)</div>
                <p className="text-sm text-muted-foreground">
                  Si <code className="text-xs">trailer_plate</code> no es NULL, debe existir una unidad registrada
                  en <code className="text-xs">units</code> cuyo <code className="text-xs">code</code> coincida.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si no existe → <strong>+1 warning por detalle</strong>
                </p>
              </div>
            </div>
          </section>

          {/* ─── CRTs Fleteros CDC ─── */}
          <section className="space-y-4 bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-900">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Tratamiento Especial: CRTs CDC Fleteros</h3>
            <div className="text-sm space-y-3 text-blue-800 dark:text-blue-200">
              <p>
                Los CRTs con prefijos <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">AR180</code>,
                <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">BR180</code>,
                <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">AR244</code>,
                <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">BR244</code>,
                <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">PY611</code> y
                <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">AR611</code> son
                considerados <strong>CRTs CDC Fleteros</strong>.
              </p>
              <p>
                Para estos CRTs, el check de choferes inexistentes se <strong>omite completamente</strong> porque:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  Durante la exportación, el sistema detecta automáticamente si es un grupo fletero
                </li>
                <li>
                  El nombre del chofer se reemplaza con <code className="text-xs bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">"FLETEROS VARIOS"</code>
                </li>
                <li>
                  No hay error real: los exports se generan correctamente
                </li>
              </ul>
              <p className="text-xs italic mt-2">
                Todos los demás checks (producto, cliente, ciudades, rutas, unidades) se aplican normalmente.
              </p>
            </div>
          </section>

          {/* ─── Fórmula SQL ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Fórmula de Cálculo (SQL)</h3>
            <p className="text-sm text-muted-foreground mb-2">
              El <code className="text-xs bg-muted px-1 py-0.5 rounded">warning_count</code> es la suma de:
            </p>
            <pre className="bg-muted p-3 rounded text-xs overflow-x-auto border border-border">
{`warning_count =
  CASE(product no existe) THEN 1 ELSE 0 END
  + CASE(client no existe) THEN 1 ELSE 0 END
  + CASE(shipper_city no existe) THEN 1 ELSE 0 END
  + CASE(recipient_city no existe) THEN 1 ELSE 0 END
  + CASE(route no existe) THEN 1 ELSE 0 END
  + SUM per detail (
      CASE(driver no existe) THEN 1 ELSE 0 END    ← omitido si CRT es CDC
      + CASE(truck no existe) THEN 1 ELSE 0 END
      + CASE(trailer no existe) THEN 1 ELSE 0 END
    )`}
            </pre>
          </section>

          {/* ─── Endpoint ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Endpoint de Recálculo</h3>
            <div className="border rounded-lg p-3 bg-muted/30">
              <p className="text-xs font-mono mb-2">
                <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 px-1 rounded mr-1">POST</span>
                /api/smart-gt/recalculate-warnings
              </p>
              <p className="text-sm text-muted-foreground">
                Recalcula el <code className="text-xs bg-muted px-1 py-0.5 rounded">warning_count</code> de
                <strong>todos</strong> los CRTs en la base de datos.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Requiere rol <code className="text-xs bg-muted px-1 py-0.5 rounded">admin</code>.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Se ejecuta automáticamente después de cada importación de datos SmartGT.
              </p>
            </div>
          </section>

          {/* ─── Visualización en UI ─── */}
          <section className="space-y-4 bg-slate-50 dark:bg-slate-950/20 p-4 rounded-lg border border-slate-200 dark:border-slate-900">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Visualización en la Interfaz</h3>
            <div className="text-sm space-y-3 text-slate-800 dark:text-slate-200">
              <p>
                El <code className="text-xs bg-slate-100 dark:bg-slate-900 px-1 py-0.5 rounded">warning_count</code> se muestra en dos lugares:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>
                  <strong>Badge circular en tabla principal</strong>: Número verde (✓) si 0, amarillo si 1-2, rojo si ≥3
                </li>
                <li>
                  <strong>Ícono de alerta en detalles expandidos</strong>: Triángulo rojo junto al nombre del campo faltante
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-1">
                    <li>Chofer inexistente → muestra ⚠ <strong>SOLO si CRT no es CDC</strong></li>
                    <li>Camión inexistente → siempre muestra ⚠</li>
                    <li>Remolque inexistente → siempre muestra ⚠</li>
                  </ul>
                </li>
              </ul>
              <p className="text-xs italic mt-2">
                La BD y la UI están sincronizadas: si un chofer CDC no se cuenta como warning en `warning_count`, tampoco se muestra el ícono en el detalle.
              </p>
            </div>
          </section>

          {/* ─── Ejemplo ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Ejemplo: CRT CDC vs Normal</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100 px-2 py-1 rounded inline-block">
                  CRT Normal (AR572-001)
                </p>
                <div className="text-xs space-y-1 pl-2">
                  <p>✓ Producto: existe</p>
                  <p>✓ Cliente: existe</p>
                  <p>✓ Ciudad origen: existe</p>
                  <p>✓ Ciudad destino: existe</p>
                  <p>✓ Ruta: existe</p>
                  <p>✗ Chofer "JUAN PÉREZ": NO existe</p>
                  <p>✓ Camión ABC123: existe</p>
                </div>
                <div className="pt-2 border-t border-red-200 dark:border-red-800">
                  <p className="text-xs font-semibold text-red-700 dark:text-red-300">warning_count = 1</p>
                  <p className="text-xs text-muted-foreground mt-1">Badge: <span className="inline-flex items-center justify-center size-4 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">1</span> (amarillo)</p>
                  <p className="text-xs text-muted-foreground">Detalle: ⚠ Ícono rojo junto al chofer</p>
                </div>
              </div>

              <div className="border rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 px-2 py-1 rounded inline-block">
                  CRT CDC Fletero (AR180-001)
                </p>
                <div className="text-xs space-y-1 pl-2">
                  <p>✓ Producto: existe</p>
                  <p>✓ Cliente: existe</p>
                  <p>✓ Ciudad origen: existe</p>
                  <p>✓ Ciudad destino: existe</p>
                  <p>✓ Ruta: existe</p>
                  <p className="text-amber-600 dark:text-amber-400">~ Chofer "JUAN PÉREZ": NO existe (omitido)</p>
                  <p>✓ Camión ABC123: existe</p>
                </div>
                <div className="pt-2 border-t border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">warning_count = 0</p>
                  <p className="text-xs text-muted-foreground mt-1">Badge: <span className="inline-flex items-center justify-center size-4 rounded-full bg-green-100 text-green-700 text-xs">✓</span> (verde)</p>
                  <p className="text-xs text-muted-foreground">Detalle: SIN ícono junto al chofer (texto gris)</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 italic">En exports → chofer reemplazado con "FLETEROS VARIOS"</p>
                </div>
              </div>
            </div>
          </section>

          {/* ─── Implementación ─── */}
          <section className="space-y-4">
            <h3 className="text-sm font-semibold">Implementación Técnica</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                <strong>Backend (API)</strong>: Función `recalculateAllWarnings()` en `smart-gt.ts`
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
                <li>Identifica CRTs CDC usando `isCDCFleteroPrefix()`</li>
                <li>Ejecuta dos UPDATE separados: uno normal, otro omitiendo driver check</li>
                <li>Resultado: `warning_count` en BD sin contar drivers CDC</li>
              </ul>

              <p className="pt-2">
                <strong>Frontend (UI)</strong>: Lógica en `SmartGTPage.tsx`
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-xs">
                <li>Función `isCDCFletero(crt)` detecta prefijos CDC</li>
                <li>Al expandir detalles, agrega el código CRT a cada detalle</li>
                <li>Render condicional: no muestra ⚠ si `isCDCFletero(d.crt)` es true</li>
                <li>Resultado: visual warnings consistentes con `warning_count`</li>
              </ul>
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
